import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { PermissionService } from '../permissions/permission.service';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { cloneDeep } from 'lodash-es';
import { PERSONAL_SPACE_GROUP_ID, personalSpaceNavigation } from '../workspace/personal-space.navigation';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _authService = inject(AuthService);
    private _permissionService = inject(PermissionService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) => {
                const roles = this._authService.roles || [];
                const navClone = cloneDeep(navigation);
                const leaveManagementGroup = cloneDeep(
                    navClone.default.find((group) => group.id === 'leave-management')
                );
                const isCompany = this._authService.isCompany();
                const isResource = roles.includes('RESOURCE');

                if (leaveManagementGroup?.children) {
                    leaveManagementGroup.children = leaveManagementGroup.children.filter((child) =>
                        isCompany
                            ? child.id === 'leave-management.approval'
                            : isResource && child.id === 'leave-management.requests'
                    );
                }

                if (roles.includes('SUPER_ADMIN')) {
                    this._filterForSuperAdmin(navClone);
                } else if (isCompany) {
                    this._filterForCompany(navClone);
                } else if (roles.includes('RESOURCE') || roles.includes('SUBCONTRACTOR')) {
                    this._filterForResource(
                        navClone,
                        roles.includes('RESOURCE') || roles.includes('SUBCONTRACTOR')
                    );
                } else if (roles.includes('CANDIDATE')) {
                    this._filterForCandidate(navClone);
                }

                // Deux entrées « Sociétés » existent dans le menu : /companies/list
                // (sociétés inscrites, réservée au super admin) et /clients/list
                // (sociétés clientes). Un seul lien doit être visible par rôle.
                if (!roles.includes('SUPER_ADMIN')) {
                    this._hideSuperAdminCompaniesEntry(navClone);
                }

                if (!isCompany) {
                    navClone.default = navClone.default.filter(
                        (group) => group.id !== 'interviews'
                    );
                }

                if (
                    leaveManagementGroup &&
                    (isCompany || isResource) &&
                    !navClone.default.some((group) => group.id === 'leave-management')
                ) {
                    const candidateIndex = navClone.default.findIndex(
                        (group) => group.id === 'candidate'
                    );
                    navClone.default.splice(
                        candidateIndex >= 0 ? candidateIndex + 1 : navClone.default.length,
                        0,
                        leaveManagementGroup
                    );
                }

                navClone.default = this._filterByPermissions(navClone.default);
                if (this._authService.hasPersonalSpace()) {
                    navClone.default.unshift(personalSpaceNavigation());
                }

                this._syncDerivedNavigations(navClone);
                this._navigation.next(navClone);
            })
        );
    }

    private _filterForSuperAdmin(navigation: Navigation): void {
        navigation.default = navigation.default.filter((group) => {
            if (group.id === 'apps' && group.title === 'CRM') {
                return true;
            }

            return false;
        });

        navigation.default.forEach((group) => {
            if (group.id === 'apps' && group.children) {
                group.children = group.children.filter(
                    (child) => child.id === 'apps.companies'
                );

                group.children.forEach((child) => {
                    if (child.children) {
                        // Filtrage par lien : les deux entrées s'intitulent
                        // « Sociétés », seule la cible les distingue.
                        child.children = child.children.filter(
                            (grandChild) => grandChild.link === '/companies/list'
                        );
                    }
                });
            }
        });
    }

    private _hideSuperAdminCompaniesEntry(navigation: Navigation): void {
        navigation.default.forEach((group) => {
            group.children?.forEach((child) => {
                if (child.id === 'apps.companies' && child.children) {
                    child.children = child.children.filter(
                        (grandChild) => grandChild.link !== '/companies/list'
                    );
                }
            });
        });
    }

    private _filterForCompany(navigation: Navigation): void {
        navigation.default = navigation.default.filter(
            (group) => group.id !== 'resource-documents'
        );
        // Filtrer le groupe "Activité" pour les companies
        navigation.default.forEach((group) => {
            if (this._isActivityGroup(group.title as string) && group.children) {
                // Pour les companies, garder CRA, Historique de CRA, Validation des CRA et Factures
                group.children = group.children.filter(
                    (child) =>
                        child.title === 'CRA' ||
                        child.title === 'Historique de CRA' ||
                        child.title === 'Validation des CRA' ||
                        child.id === 'apps.factures'
                );
            }

            if (group.id === 'candidate' && group.title === 'Candidats' && group.children) {
                group.children = group.children.filter(
                    (child) =>
                        child.link === '/candidates/listCandidate'
                );
            }
        });
    }

    private _filterForResource(navigation: Navigation, includeDocuments = false): void {
        navigation.default = navigation.default.filter((group) => {
            if (includeDocuments && group.id === 'resource-documents') {
                return true;
            }

            if (group.id === 'ticket-management') {
                if (group.children) {
                    group.children = group.children.filter(
                        (child) => child.id === 'apps.projects'
                    );
                }
                return true;
            }

            if (this._isActivityGroup(group.title as string)) {
                if (group.children) {
                    group.children = group.children.filter((child) => {
                        const commonItems = [
                            'CRA',
                            'Historique de CRA',
                            'Note de frais',
                            'Suivi du chiffre d\'affaires',
                        ];

                        return commonItems.includes(child.title as string);
                    });
                }
                return true;
            }
            return false;
        }).sort((a, b) => {
            // Priorité pour le groupe "Activité"
            if (this._isActivityGroup(a.title as string)) {
                return -1; // Mettre "Activité" en premier
            }
            if (this._isActivityGroup(b.title as string)) {
                return 1; // Mettre "Activité" en premier
            }
            return 0; // Pas de changement pour les autres groupes
        }
        );
    }

    private _filterForCandidate(navigation: Navigation): void {
        navigation.default.forEach((group) => {
            if (group.children && this._isActivityGroup(group.title as string)) {
                group.children = group.children.filter(
                    (child) =>
                        child.title !== 'Note de frais' &&
                        child.title !== 'Documents'
                );
            }

            if (group.id === 'candidate' && group.title === 'Candidats' && group.children) {
                group.children = group.children.filter(
                    (child) => child.link === '/candidates/list'
                );
            }
        });
    }

    /** Retire les entrées dont le droit (meta.permission) manque, puis les groupes devenus vides. */
    private _filterByPermissions(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items
            .filter((item) => this._isAllowed(item))
            .map((item) => item.children
                ? { ...item, children: this._filterByPermissions(item.children) }
                : item)
            .filter((item) => !item.children || item.children.length > 0 || !!item.link);
    }

    /**
     * Un compte société (propriétaire ou collaborateur) doit avoir le droit ; les autres comptes gardent
     * leurs entrées habituelles, sauf celles réservées aux sociétés (meta.companyOnly). Les modules hors
     * catalogue des droits (meta.hiddenForCollaborators) sont réservés au propriétaire.
     */
    private _isAllowed(item: FuseNavigationItem): boolean {
        const restricted = this._authService.isManager() || this._permissionService.previewRoleName !== null;
        if (item.meta?.hiddenForCollaborators && restricted) {
            return false;
        }
        const permission = item.meta?.permission;
        if (!permission) {
            return true;
        }
        return this._authService.isCompany()
            ? this._permissionService.can(permission)
            : !item.meta.companyOnly;
    }

    private _isActivityGroup(title?: string): boolean {
        return !!title && title.toLowerCase().startsWith('activit');
    }

    private _syncDerivedNavigations(navigation: Navigation): void {
        const defaultIds = navigation.default.map((item) => item.id);

        ['compact', 'futuristic', 'horizontal'].forEach((navType) => {
            const navItems = navigation[navType];
            const syncedItems = navItems
                .filter((item) => defaultIds.includes(item.id))
                .map((item) => {
                    const defaultItem = navigation.default.find(
                        (def) => def.id === item.id
                    );
                    return {
                        ...item,
                        children: cloneDeep(defaultItem?.children ?? []),
                    };
                });

            // Groupes ajoutés à la volée, absents des autres variantes du menu.
            [PERSONAL_SPACE_GROUP_ID, 'leave-management'].forEach((groupId) => {
                const group = navigation.default.find((item) => item.id === groupId);
                if (group && !syncedItems.some((item) => item.id === groupId)) {
                    syncedItems.push(cloneDeep(group));
                }
            });

            navigation[navType] = syncedItems;
        });
    }
}
